import React, { useState } from "react"
import { bodyRequest, pathname }      from '@helpers/javascript/javascript'
import { StepOne }                    from "./stepOne/stepOne"
import { StepTwo }                    from "./stepTwo/stepTwo"
import { convertKeysToSnake }         from "@react/helpers/helpers"

export const TYPES =
  {
    reactive: 'Reactive',
    preventive: 'Preventive',
    installation: 'Installation'
  }

const App = props => {
  const {
    workorder,
    categories,
    priorities,
    titleFromCategory,
    managers,
    technicians,
    checkboxes,
    calendar,
    storehouses,
    taskBlueprints,
    displayUnpaid,
    includeMbcContracts,
    allowRecurrency,
    availableMaterials,
    calendarPreferences,
    maximumDuration,
    uploadMaxSizeMb,
    partners
  } = props

  const [workorderState, setWorkorder]      = useState(workorder)
  const [currentStep,    setCurrentStep]    = useState(1)
  const [siteMaterials,  setSiteMaterials]  = useState(availableMaterials)
  const [isSubmitting,   setIsSubmitting]   = useState(false)
  
  const setWorkorderState = newState => {
    setWorkorder(prevState => ({ ...prevState, ...newState}))
  }

  const handleSubmit = (form, upload) => {
    if(!form.reportValidity()) return
    setIsSubmitting(true)
    let workorder = { ...workorderState }
    workorder.closedStatus                = workorderState.closedStatus ? 'Under contract' : null
    workorder.extraFieldAnswersAttributes = { ...workorderState.extraFieldAnswers }
    workorder.invoiceLinesAttributes      = { ...workorderState.invoiceLines }
    if (workorderState.workorderType != TYPES.preventive ) {
      workorder.maintenanceTasksAttributes = workorderState.materials.flatMap(material => {
        const operations = workorderState.maintenanceTasks.filter(task => task.materialId == material && task.taskBlueprintId)
        if (operations.length == 0) return [{ materialId: material }]

        return operations.map(task => ({ materialId: material, taskBlueprintId: task.taskBlueprintId, predictedDuration: task.predictedDuration }))
      })
    }

    const path   = '/workorders'
    const body   = {
      upload:    !!upload,
      demand:    workorderState.demandId,
      workorder: convertKeysToSnake(workorder),
      passages:  convertKeysToSnake(workorderState.passages),
      email:     { technician: workorderState.emailTechnician,
                   requester:  workorderState.emailRequester },
      photo:     { images: workorderState.photos.map(photo => photo.signedId) },
      files:     workorderState.files.map(file => { return { legend: file.filename, file: file.signedId } })
    }

    if (workorderState.workorderType == TYPES.preventive ) {
      body.maintenance_plan = convertKeysToSnake({maintenance_tasks_attributes: workorderState.maintenanceTasks })
      if (workorderState.recurrency) {
        body.maintenance_plan = convertKeysToSnake({ ...body.maintenance_plan, ...workorderState.rrule, addRecurrency: true })
      }
    }

    const url = pathname({ path })
    bodyRequest({ url, body }).then(res => {
      window.location.href = (res.path)
    }).catch(err => setIsSubmitting(false))
  }

  const changeTechnician = technicianId => {
    if (technicianId) {
      setWorkorderState({technicianId: technicianId })
    } else {
      setWorkorderState({technicianId: technicianId, secondaryTechniciansIds: [] })
    }
  }

  const changeAssets = assets => {
    const site          = assets.site
    const materials     = site.materials

    const siteChanged = workorderState.siteId !== site.id
    // AssetSelect emits onChange whenever its internal state updates. When the
    // client picker changes, marché info is fetched async — during that window
    // assets.mbc_contract is {} even though the user didn't unset anything.
    // Treat that empty payload as "no information" and keep the existing
    // mbcContractId rather than wiping it (and the invoice lines with it).
    // An explicit user pick or reset of the MBC dropdown is tagged with
    // `explicit: true` by AssetSelect; that always overrides the current value.
    const clientChanged    = workorderState.clientId !== assets.client.id
    const explicitMbcReset = assets.mbc_contract?.explicit && !assets.mbc_contract.id
    const newMbcContractId =
      assets.mbc_contract?.id           ? assets.mbc_contract.id :
      explicitMbcReset || clientChanged ? null                    :
                                          workorderState.mbcContractId
    const newMbcAllowOffBpu =
      assets.mbc_contract?.id           ? !!assets.mbc_contract.allow_off_bpu :
      explicitMbcReset || clientChanged ? false                                :
                                          workorderState.mbcAllowOffBpu
    const mbcChanged = workorderState.mbcContractId !== newMbcContractId
    // A financial contract is bound to one client and one site, and this form has
    // no contract picker: the prefilled link only holds while both stay put.
    const keepContract = !clientChanged && !siteChanged

    if (siteChanged) {
      if (materials) {
        setSiteMaterials(materials.map(material => ({ materialId: material.id, name: material.name })))
      } else { setSiteMaterials([])}
    }
    setWorkorderState(
      {
        workorderType:    siteChanged ? TYPES.reactive : workorderState.workorderType,
        clientId:         assets.client.id,
        clientName:       assets.client.name,
        clientPartnerId:  assets.client.partner?.id || null,
        siteId:           site.id,
        siteName:         site.name,
        address:          assets.site.address,
        contactId:        assets.contact.id,
        contractId:       keepContract ? workorderState.contractId    : null,
        contractTitle:    keepContract ? workorderState.contractTitle : null,
        mbcContractId:    newMbcContractId,
        mbcAllowOffBpu:   newMbcAllowOffBpu,
        // Marché identity drives line-picker semantics: linking, switching, or
        // unlinking it makes the existing lines stale (off-BPU products that
        // should have been BPU, or BPU lines tied to a different marché).
        invoiceLines:     mbcChanged ? [] : workorderState.invoiceLines,
        materials:        siteChanged ? [] : workorderState.materials,
        maintenanceTasks: siteChanged ? [] : workorderState.maintenanceTasks,
        technicianId:     site.default_technician_id || workorderState.technicianId
      }
    )
  }

  return(
    <>
      {
        currentStep === 1 &&
          <StepOne
            workorderState          = { workorderState }
            setWorkorderState       = { setWorkorderState }
            categories              = { categories.categories }
            categoryRequired        = { categories.mandatory }
            priorities              = { priorities }
            titleFromCategory       = { titleFromCategory }
            checkboxes              = { checkboxes }
            categoryFields          = { categories.categoryFields }
            setCurrentStep          = { setCurrentStep }
            storehouses             = { storehouses }
            blueprints              = { taskBlueprints }
            siteMaterials           = { siteMaterials }
            changeAssets            = { changeAssets }
            displayUnpaid           = { displayUnpaid }
            includeMbcContracts     = { includeMbcContracts }
            allowRecurrency         = { allowRecurrency }
            handleSubmit            = { handleSubmit }
            managers                = { managers }
            technicians             = { technicians }
            isSubmitting            = { isSubmitting }
            changeTechnician        = { changeTechnician }
            maximumDuration         = { maximumDuration }
            maxSizeMb               = { uploadMaxSizeMb }
            partners                = { partners }
          />
      }
      {
        currentStep === 2 &&
          <StepTwo
            workorderState      = { workorderState }
            setWorkorderState   = { setWorkorderState }
            managers            = { managers }
            technicians         = { technicians }
            setCurrentStep      = { setCurrentStep }
            calendar            = { calendar }
            handleSubmit        = { handleSubmit }
            isSubmitting        = { isSubmitting }
            calendarPreferences = { calendarPreferences }
            changeTechnician    = { changeTechnician }
            maximumDuration     = { maximumDuration }
          />
      }
    </>
  )
}

export default App
